import { supabase } from '@/lib/supabase';
import { FamilyMember, FamilyMemberRole } from '@/types/models';
import { InviteStatus } from '@/types/constants';
import { mapToInviteStatus } from '@/utils/typeUtils';
import { EmailService } from '@/services/emailService';
import { fetchOrganizationData } from '@/utils/auth/organizationData';

// Helper to safely access nested properties
const safeUserProperty = (user: any, property: string, defaultValue: string = '') => {
  if (!user) return defaultValue;
  return user[property] || defaultValue;
};

export const getFamilyMembers = async (familyId: string): Promise<FamilyMember[]> => {
  try {
    const { data, error } = await supabase
      .from('family_members')
      .select('*, user:user_id(*)')
      .eq('family_id', familyId);

    if (error) throw error;
    
    // Map the data to include user information if available
    return (data || []).map(member => {
      return {
        id: member.id,
        family_id: member.family_id,
        user_id: member.user_id,
        role: member.role,
        invite_status: mapToInviteStatus(member.invite_status),
        is_primary_billing: member.is_primary_billing,
        can_edit_profile: member.can_edit_profile,
        invited_email: member.invited_email,
        invited_by_user_id: member.invited_by_user_id,
        created_at: member.created_at,
        user: member.user ? {
          id: safeUserProperty(member.user, 'id'),
          email: safeUserProperty(member.user, 'email')
        } : undefined
      } as FamilyMember;
    });
  } catch (error) {
    console.error('Error fetching family members:', error);
    return [];
  }
};

export const addFamilyMember = async (
  familyId: string,
  role: FamilyMemberRole = 'parent',
  isPrimaryBilling: boolean = false,
  canEditProfile: boolean = true,
  invitedEmail?: string,
  userId?: string
): Promise<FamilyMember | null> => {
  try {
    // Insert the new family member with a valid invite_status
    // Ensure we're using a string literal for invite_status that matches the allowed values
    const { data: newMember, error } = await supabase
      .from('family_members')
      .insert({
        family_id: familyId,
        user_id: userId,
        role: role,
        invite_status: 'pending', // Use string literal instead of InviteStatus type
        is_primary_billing: isPrimaryBilling,
        can_edit_profile: canEditProfile,
        invited_email: invitedEmail,
        invited_by_user_id: userId, // Could be the same user who's adding themselves
      })
      .select('*, user:user_id(*)')
      .single();

    if (error) throw error;
    
    return {
      ...newMember,
      user: newMember.user ? {
        id: safeUserProperty(newMember.user, 'id'),
        email: safeUserProperty(newMember.user, 'email')
      } : undefined,
      invite_status: mapToInviteStatus(newMember.invite_status)
    } as FamilyMember;
  } catch (error) {
    console.error('Error adding family member:', error);
    return null;
  }
};

export const updateFamilyMember = async (
  memberId: string, 
  updates: { 
    role?: FamilyMemberRole; 
    isPrimaryBilling?: boolean; 
    canEditProfile?: boolean 
  }
): Promise<FamilyMember | null> => {
  try {
    const updateData: any = {};
    if (updates.role) updateData.role = updates.role;
    if (updates.isPrimaryBilling !== undefined) updateData.is_primary_billing = updates.isPrimaryBilling;
    if (updates.canEditProfile !== undefined) updateData.can_edit_profile = updates.canEditProfile;
    
    const { data: updatedMember, error } = await supabase
      .from('family_members')
      .update(updateData)
      .eq('id', memberId)
      .select('*, user:user_id(*)')
      .single();
      
    if (error) throw error;
    
    return {
      ...updatedMember,
      user: updatedMember.user ? {
        id: safeUserProperty(updatedMember.user, 'id'),
        email: safeUserProperty(updatedMember.user, 'email')
      } : undefined,
      invite_status: mapToInviteStatus(updatedMember.invite_status)
    } as FamilyMember;
  } catch (error) {
    console.error('Error updating family member:', error);
    return null;
  }
};

export const removeFamilyMember = async (memberId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('family_members')
      .delete()
      .eq('id', memberId);
      
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error removing family member:', error);
    return false;
  }
};

export const sendFamilyMemberInvite = async (memberId: string): Promise<boolean> => {
  try {
    // First get the member details to get their email
    const { data: member, error: memberError } = await supabase
      .from('family_members')
      .select('invited_email, family_id')
      .eq('id', memberId)
      .single();

    if (memberError) throw memberError;
    
    if (!member || !member.invited_email) {
      console.error('Cannot send invite: No email address found for family member');
      return false;
    }

    // Get family name and organization_id for the invitation email
    const { data: family, error: familyError } = await supabase
      .from('families')
      .select('name, organization_id')
      .eq('id', member.family_id)
      .single();

    if (familyError) throw familyError;

    if (!family || !family.organization_id) {
      console.error('Cannot send invite: Family has no organization ID');
      return false;
    }

    // Fetch organization name
    let organizationName = undefined;
    try {
      const orgData = await fetchOrganizationData(family.organization_id);
      organizationName = orgData?.name;
    } catch (orgErr) {
      console.warn('Could not fetch organization name for invite:', orgErr);
    }

    // Update the family member status to pending
    const { error } = await supabase
      .from('family_members')
      .update({ invite_status: "pending" })
      .eq('id', memberId);

    if (error) throw error;

    // Send the invitation email using the EmailService with token-based approach
    const metadata = {
      family_id: member.family_id,
      familyName: family?.name || 'Family account',
      memberId: memberId,
      role: 'parent',
      organization_id: family.organization_id,
      organizationName,
      // Add any other metadata needed for the invite
      firstName: '', // Could be fetched if available
      lastName: '',
      created_by: (await supabase.auth.getUser()).data.user?.id
    };

    const { success, error: emailError } = await EmailService.sendInvite(
      member.invited_email,
      metadata
    );

    if (emailError) {
      console.error('Email sending failed:', emailError);
      // We don't throw here as we've already updated the status
    }
    
    return success;
  } catch (error) {
    console.error('Error sending invitation:', error);
    return false;
  }
};
